import { ReactNode, useEffect, useState } from 'react';
import { DefaultEditor } from '@drodil/backstage-plugin-toolbox';
import {
  Chip,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import { repair, RepairFormat, RepairSummaryItem } from './iamPolicyRepair';

const useStyles = makeStyles(theme => ({
  panel: {
    width: '100%',
    minHeight: theme.spacing(40),
    padding: theme.spacing(2),
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
  },
  count: {
    marginLeft: theme.spacing(1),
    fontWeight: 'bold',
  },
}));

const sample = `{
  // policy éditée à la main, quelques coquilles se sont glissées
  version: '2012-10-17',
  Statement: [
    {
      Sid: 'S3StateObjects',
      effect: 'allow',
      Action: ['s3:GetObject', 's3:GetObject', 's3:PutObject'],
      resource: 'arn:aws:s3:::my-bucket/*',
    },
    {
      sid: "Ec2Tags",
      Action: 'ec2:CreateTags',
      Resource: ['arn:aws:ec2:*:*:instance/*',],
    },
  ],
}`;

const formatLabel = (format: RepairFormat): string =>
  format === 'terraform' ? 'Terraform statements' : 'Policy IAM';

export const IamPolicyRepairTool = () => {
  const classes = useStyles();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [fixes, setFixes] = useState<RepairSummaryItem[]>([]);
  const [format, setFormat] = useState<RepairFormat | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      setFixes([]);
      setFormat(null);
      setError('');
      return;
    }
    try {
      const result = repair(input);
      setOutput(result.output);
      setFixes(result.fixes);
      setFormat(result.format);
      setError('');
    } catch (e) {
      setOutput('');
      setFixes([]);
      setFormat(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [input]);

  let panelContent: ReactNode;
  if (error) {
    panelContent = <Typography className={classes.error}>{error}</Typography>;
  } else if (!input.trim()) {
    panelContent = (
      <Typography color="textSecondary">
        Collez un fichier à réparer : les corrections appliquées s'afficheront
        ici.
      </Typography>
    );
  } else if (fixes.length === 0) {
    panelContent = (
      <Typography color="textSecondary">
        Aucune correction nécessaire, le fichier est déjà valide.
      </Typography>
    );
  } else {
    panelContent = (
      <List dense disablePadding>
        {fixes.map(fix => (
          <ListItem key={fix.message} disableGutters>
            <ListItemText
              primary={
                <>
                  {fix.message}
                  {fix.count > 1 && (
                    <span className={classes.count}>×{fix.count}</span>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  }

  const panel = (
    <Paper variant="outlined" className={classes.panel}>
      <div className={classes.header}>
        <Typography variant="subtitle2">Corrections appliquées</Typography>
        {format && (
          <Chip size="small" label={`Format : ${formatLabel(format)}`} />
        )}
      </div>
      {panelContent}
    </Paper>
  );

  return (
    <DefaultEditor
      input={input}
      setInput={setInput}
      output={output}
      sample={sample}
      inputLabel="Fichier à réparer"
      outputLabel="Fichier corrigé"
      rightContent={panel}
      allowFileUpload
      acceptFileTypes=".json,.tf,.txt"
      allowFileDownload
      downloadFileName="repaired.json"
      downloadFileType="application/json"
    />
  );
};

export default IamPolicyRepairTool;
